import React, { FC, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'

const ControlsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  color: white;
  font-size: 10px;
`

const ControlSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const KeyboardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 3px;
  width: 120px;
`

const Key = styled.div<{ $span?: number; $active?: boolean }>`
  background: ${({ $active }) =>
    $active ? 'rgba(255, 255, 255, 0.3)' : 'rgba(255, 255, 255, 0.1)'};
  border: 1px solid
    ${({ $active }) =>
      $active ? 'rgba(255, 255, 255, 0.5)' : 'rgba(255, 255, 255, 0.25)'};
  border-radius: 3px;
  padding: 4px 6px;
  text-align: center;
  font-weight: 600;
  font-size: 10px;
  min-width: 18px;
  height: 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  transition:
    background 0.1s,
    border-color 0.1s;
  ${({ $span }) => $span && `grid-column: span ${$span};`}
`

const KeyLabel = styled.span`
  font-size: 8px;
  opacity: 0.6;
  line-height: 1;
`

const MouseRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const MouseSvg = styled.svg`
  width: 36px;
  height: 56px;
`

const MouseLabels = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 9px;
  opacity: 0.8;
`

export const ControlsHelp: FC = () => {
  const [pressedKeys, setPressedKeys] = useState<Set<string>>(new Set())
  const [mouseState, setMouseState] = useState({
    left: false,
    right: false,
    scroll: false,
  })
  const scrollTimeout = useRef<number | null>(null)

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const key = e.key.toLowerCase()
      setPressedKeys((prev) => new Set(prev).add(key))
    }

    const handleKeyUp = (e: KeyboardEvent) => {
      const key = e.key.toLowerCase()
      setPressedKeys((prev) => {
        const next = new Set(prev)
        next.delete(key)
        return next
      })
    }

    const handleMouseDown = (e: MouseEvent) => {
      if (e.button === 0) setMouseState((s) => ({ ...s, left: true }))
      if (e.button === 2) setMouseState((s) => ({ ...s, right: true }))
    }

    const handleMouseUp = (e: MouseEvent) => {
      if (e.button === 0) setMouseState((s) => ({ ...s, left: false }))
      if (e.button === 2) setMouseState((s) => ({ ...s, right: false }))
    }

    const handleWheel = () => {
      setMouseState((s) => ({ ...s, scroll: true }))
      if (scrollTimeout.current) clearTimeout(scrollTimeout.current)
      scrollTimeout.current = window.setTimeout(() => {
        setMouseState((s) => ({ ...s, scroll: false }))
      }, 150)
    }

    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener('keyup', handleKeyUp)
    window.addEventListener('mousedown', handleMouseDown)
    window.addEventListener('mouseup', handleMouseUp)
    window.addEventListener('wheel', handleWheel)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('keyup', handleKeyUp)
      window.removeEventListener('mousedown', handleMouseDown)
      window.removeEventListener('mouseup', handleMouseUp)
      window.removeEventListener('wheel', handleWheel)
      if (scrollTimeout.current) clearTimeout(scrollTimeout.current)
    }
  }, [])

  const isActive = (keys: string[]) => keys.some((k) => pressedKeys.has(k))

  const activeColor = 'rgba(255, 255, 255, 0.3)'
  const inactiveColor = 'rgba(255, 255, 255, 0.1)'
  const activeStroke = 'rgba(255, 255, 255, 0.5)'
  const inactiveStroke = 'rgba(255, 255, 255, 0.25)'

  return (
    <ControlsContainer>
      <ControlSection>
        <KeyboardGrid>
          <Key $active={isActive(['q'])}>
            Q<KeyLabel>down</KeyLabel>
          </Key>
          <Key $active={isActive(['w', 'arrowup'])}>W ↑</Key>
          <Key $active={isActive(['e'])}>
            E<KeyLabel>up</KeyLabel>
          </Key>
          <Key $active={isActive(['a', 'arrowleft'])}>A ←</Key>
          <Key $active={isActive(['s', 'arrowdown'])}>S ↓</Key>
          <Key $active={isActive(['d', 'arrowright'])}>D →</Key>
          <Key $active={isActive(['shift'])}>
            SHIFT<KeyLabel>down</KeyLabel>
          </Key>
          <Key $span={2} $active={isActive([' '])}>
            SPACE<KeyLabel>up</KeyLabel>
          </Key>
        </KeyboardGrid>
      </ControlSection>

      <ControlSection>
        <MouseRow>
          <MouseSvg viewBox='0 0 36 56'>
            {/* Mouse body outline */}
            <rect
              x='2'
              y='2'
              width='32'
              height='52'
              rx='16'
              fill='none'
              stroke={inactiveStroke}
              strokeWidth='1.5'
            />
            {/* Left button */}
            <path
              d='M2 18 L2 16 Q2 2 18 2 L18 18 Z'
              fill={mouseState.left ? activeColor : inactiveColor}
              stroke={mouseState.left ? activeStroke : inactiveStroke}
              strokeWidth='1'
            />
            {/* Right button */}
            <path
              d='M34 18 L34 16 Q34 2 18 2 L18 18 Z'
              fill={mouseState.right ? activeColor : inactiveColor}
              stroke={mouseState.right ? activeStroke : inactiveStroke}
              strokeWidth='1'
            />
            {/* Scroll wheel */}
            <rect
              x='15'
              y='8'
              width='6'
              height='12'
              rx='3'
              fill={mouseState.scroll ? activeColor : inactiveColor}
              stroke={mouseState.scroll ? activeStroke : inactiveStroke}
              strokeWidth='1'
            />
            {/* Divider line */}
            <line
              x1='18'
              y1='2'
              x2='18'
              y2='18'
              stroke={inactiveStroke}
              strokeWidth='0.5'
            />
          </MouseSvg>
          <MouseLabels>
            <span>Left: Rotate</span>
            <span>Right: Pan</span>
            <span>Scroll: Zoom</span>
            <span>Double-click: Set pivot</span>
          </MouseLabels>
        </MouseRow>
      </ControlSection>
    </ControlsContainer>
  )
}
